// @greenlight-spec: 28-it-dashboard-ui.md
// @greenlight-spec: decisions/0032-app-tags.md
import * as React from 'react';

import { type TagMode } from '@/components/apps-list/filtering';
import { FilterPill } from '@/components/greenlight/FilterPill';
import { cn } from '@/lib/utils';

/** The minimal shape TagChips derives its tag/count list from — AppListItem
 *  and MarketplaceApp both satisfy this structurally. `status` is optional so
 *  callers with no status concept (MarketplaceApp) simply never hit the
 *  `deleted`-exclusion branch below. */
export interface TagChipsItem {
  status?: string;
  tags?: readonly string[];
}

interface TagChipsProps {
  /** Loaded apps — the available tags + counts are derived from these. */
  items: readonly TagChipsItem[];
  activeTags: string[];
  tagMode: TagMode;
  onToggleTag: (tag: string) => void;
  onTagModeChange: (mode: TagMode) => void;
}

/**
 * Multi-select tag filter for the apps list (ADR-0032). The available tags are
 * derived from the loaded fleet (like the status buckets), so the row only shows
 * tags in play. Selecting two or more reveals the Any/All toggle — inclusive
 * (match any selected tag) vs exclusive (match all). Tags use the primary accent
 * so the row reads as distinct from the status chips above it.
 */
export function TagChips({
  items,
  activeTags,
  tagMode,
  onToggleTag,
  onTagModeChange,
}: TagChipsProps): React.JSX.Element | null {
  const tagCounts = React.useMemo(() => {
    const counts = new Map<string, number>();
    for (const app of items) {
      if (app.status === 'deleted') continue; // archived apps aren't in the live fleet
      for (const tag of app.tags ?? []) counts.set(tag, (counts.get(tag) ?? 0) + 1);
    }
    return counts;
  }, [items]);

  const tags = React.useMemo(
    () => [...tagCounts.keys()].sort((a, b) => a.localeCompare(b)),
    [tagCounts],
  );

  // No tags in the loaded fleet → nothing to filter by; render nothing.
  if (tags.length === 0) return null;

  const activeSet = new Set(activeTags.map((t) => t.toLowerCase()));

  return (
    <div className="flex flex-wrap items-center gap-1.5" role="group" aria-label="Filter by tag">
      <span className="font-mono text-[10px] uppercase tracking-[0.08em] text-muted-foreground">
        Tags
      </span>
      {tags.map((tag) => {
        const isActive = activeSet.has(tag.toLowerCase());
        return (
          <FilterPill key={tag} selected={isActive} onClick={() => onToggleTag(tag)}>
            {tag} <span className="tabular-nums">{tagCounts.get(tag)}</span>
          </FilterPill>
        );
      })}

      {activeTags.length >= 2 && (
        <div
          className="ml-1 inline-flex items-center rounded-full border border-border bg-card p-0.5"
          role="group"
          aria-label="Tag match mode"
        >
          {(['any', 'all'] as const).map((mode) => (
            <button
              key={mode}
              type="button"
              aria-pressed={tagMode === mode}
              onClick={() => onTagModeChange(mode)}
              className={cn(
                'h-6 rounded-full px-2.5 text-[11px] font-medium transition-colors',
                tagMode === mode
                  ? 'bg-foreground text-background'
                  : 'text-muted-foreground hover:text-foreground',
              )}
            >
              {mode === 'any' ? 'Any' : 'All'}
            </button>
          ))}
        </div>
      )}
    </div>
  );
}
